import { useEffect } from "react";

/**
 * Lightweight SEO helper: sets <title>, meta description, OG tags, a
 * <link rel="canonical">, and one or more JSON-LD <script>s for structured
 * data. No deps (avoids react-helmet).
 *
 * Cleans up on unmount so SPAs don't leak meta from one page to the next.
 *
 * Props:
 *   - title           page <title> and og:title
 *   - description     meta description / og / twitter description
 *   - imageUrl        og:image + twitter:image (absolute URL recommended)
 *   - url             canonical link + og:url (defaults to current href)
 *   - type            og:type (default "website"; use "product"/"profile"/"article")
 *   - jsonLd          object OR array of structured-data objects
 *   - breadcrumbs     [{name, url}] — emits a BreadcrumbList JSON-LD
 *   - noindex         when true emits <meta name="robots" content="noindex">
 */
export default function SeoHead({
  title,
  description = "",
  imageUrl = "",
  url = typeof window !== "undefined" ? window.location.href : "",
  jsonLd = null,
  breadcrumbs = null,
  type = "website",
  noindex = false,
}) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const setMeta = (key, val, attr = "name") => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      const created = !el;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", val || "");
      el.dataset.seohead = created ? "1" : el.dataset.seohead || "";
      return el;
    };

    const dynamic = [];
    if (description) {
      dynamic.push(setMeta("description", description));
      dynamic.push(setMeta("og:description", description, "property"));
      dynamic.push(setMeta("twitter:description", description));
    }
    if (title) {
      dynamic.push(setMeta("og:title", title, "property"));
      dynamic.push(setMeta("twitter:title", title));
    }
    if (imageUrl) {
      dynamic.push(setMeta("og:image", imageUrl, "property"));
      dynamic.push(setMeta("twitter:image", imageUrl));
      dynamic.push(setMeta("twitter:card", "summary_large_image"));
    }
    if (url) dynamic.push(setMeta("og:url", url, "property"));
    dynamic.push(setMeta("og:type", type, "property"));
    if (noindex) dynamic.push(setMeta("robots", "noindex, nofollow"));

    // Canonical link tag
    let canonical = document.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical?.getAttribute("href");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    if (url) canonical.setAttribute("href", url);

    // Inject JSON-LD blocks (page-scoped — distinct from site-wide ones
    // shipped in index.html). Tagged with data-seohead-jsonld for cleanup.
    const scripts = [];
    const blocks = [];
    if (jsonLd) blocks.push(...(Array.isArray(jsonLd) ? jsonLd : [jsonLd]));
    if (breadcrumbs && breadcrumbs.length) {
      blocks.push({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((b, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: b.name,
          item: b.url,
        })),
      });
    }
    for (const b of blocks) {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.text = JSON.stringify(b);
      s.dataset.seoheadJsonld = "1";
      document.head.appendChild(s);
      scripts.push(s);
    }

    return () => {
      document.title = prevTitle;
      for (const s of scripts) s.remove();
      if (prevCanonical !== undefined && canonical) {
        canonical.setAttribute("href", prevCanonical);
      }
    };
  }, [title, description, imageUrl, url, type, jsonLd, breadcrumbs, noindex]);
  return null;
}
